import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronRight } from "lucide-react";
import { Seo, breadcrumbLd, ORGANIZATION_LD } from "./Seo";
import { Reveal } from "./shared";
import { BLOG_POSTS } from "../../data/blog";

export function BlogIndex() {
    return (
        <section className="pt-32 sm:pt-40 pb-24 sm:pb-32 relative overflow-hidden" data-testid="blog-index-page">
            <Seo
                title="Blog — AI Automation Guides for Service Businesses | Flowtech"
                description="Practical, plain-English guides on AI employees, lead follow-up automation, appointment scheduling, and business automation for service businesses."
                path="/blog"
                jsonLd={[ORGANIZATION_LD, breadcrumbLd([{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }])]}
            />
            <div className="absolute top-0 left-1/4 w-[500px] h-[400px] bg-cyan-500/[0.06] blur-[140px] rounded-full pointer-events-none" />
            <div className="container-x relative max-w-4xl">
                <Reveal>
                    <p className="ff-mono text-xs uppercase tracking-[0.25em] text-cyan-400 mb-4">Flowtech Blog</p>
                </Reveal>
                <Reveal delay={0.08}>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-[1.1]">
                        AI Automation, Explained in Plain English
                    </h1>
                </Reveal>
                <Reveal delay={0.14}>
                    <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed max-w-2xl">
                        Practical guides for service business owners on AI employees, lead follow-up, scheduling, and removing repetitive admin work.
                    </p>
                </Reveal>
                <div className="mt-14 flex flex-col gap-5">
                    {BLOG_POSTS.map((post, i) => (
                        <Reveal key={post.slug} delay={i * 0.08}>
                            <Link
                                to={`/blog/${post.slug}`}
                                className="glass card-hover rounded-3xl p-7 sm:p-8 block group"
                                data-testid={`blog-card-${post.slug}`}
                            >
                                <p className="ff-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                    {new Date(post.date + "T00:00:00").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })} · {post.readTime}
                                </p>
                                <h2 className="mt-3 text-xl sm:text-2xl font-bold text-white tracking-tight group-hover:text-cyan-200 transition-colors duration-300">
                                    {post.title}
                                </h2>
                                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{post.excerpt}</p>
                                <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-cyan-300">
                                    Read article
                                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                                </span>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

export function BlogPost() {
    const { slug } = useParams();
    const post = BLOG_POSTS.find((p) => p.slug === slug);

    if (!post) {
        return (
            <section className="pt-40 pb-32 container-x" data-testid="blog-not-found">
                <h1 className="text-3xl font-extrabold text-white">Article not found</h1>
                <Link to="/blog" className="btn-ghost mt-8">Back to the blog</Link>
            </section>
        );
    }

    const jsonLd = [
        {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.seoDesc,
            datePublished: post.date,
            author: { "@type": "Organization", name: "Flowtech", url: "https://flowtechsolutions.site" },
            publisher: { "@type": "Organization", name: "Flowtech", url: "https://flowtechsolutions.site", logo: { "@type": "ImageObject", url: "https://flowtechsolutions.site/favicon.svg" } },
            mainEntityOfPage: `https://flowtechsolutions.site/blog/${post.slug}`,
        },
        breadcrumbLd([{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }, { name: post.title, path: `/blog/${post.slug}` }]),
    ];

    return (
        <article className="pt-32 sm:pt-40 pb-24 sm:pb-32 relative overflow-hidden" data-testid={`blog-post-${post.slug}`}>
            <Seo title={post.seoTitle} description={post.seoDesc} path={`/blog/${post.slug}`} jsonLd={jsonLd} />
            <div className="absolute top-0 left-1/4 w-[500px] h-[400px] bg-cyan-500/[0.06] blur-[140px] rounded-full pointer-events-none" />
            <div className="container-x relative max-w-3xl">
                <Reveal>
                    <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-slate-500 mb-8 flex-wrap">
                        <Link to="/" className="hover:text-cyan-300 transition-colors duration-300">Home</Link>
                        <ChevronRight className="w-3 h-3" />
                        <Link to="/blog" className="hover:text-cyan-300 transition-colors duration-300">Blog</Link>
                        <ChevronRight className="w-3 h-3" />
                        <span className="text-slate-400">{post.title}</span>
                    </nav>
                </Reveal>
                <Reveal delay={0.06}>
                    <p className="ff-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-4">
                        {new Date(post.date + "T00:00:00").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })} · {post.readTime}
                    </p>
                </Reveal>
                <Reveal delay={0.1}>
                    <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white leading-[1.15]">{post.title}</h1>
                </Reveal>
                <Reveal delay={0.14}>
                    <div className="mt-6 h-px w-full bg-gradient-to-r from-cyan-400/40 via-white/10 to-transparent" />
                </Reveal>
                {post.body.map((b, i) =>
                    b.h2 ? (
                        <h2 key={i} className="mt-10 text-xl sm:text-2xl font-bold text-white tracking-tight">{b.h2}</h2>
                    ) : (
                        <p key={i} className="mt-5 text-base text-slate-400 leading-relaxed">{b.p}</p>
                    )
                )}
                <div className="mt-14 glass rounded-3xl p-8 sm:p-10">
                    <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">See How Flowtech Can Help</h2>
                    <p className="mt-3 text-sm text-slate-400 leading-relaxed max-w-md">
                        Plans start at $299/month with no setup fee. Start a free chat and we'll show you exactly what your business can automate.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3">
                        <Link to="/#book" className="btn-primary" data-testid={`blog-cta-${post.slug}`}>
                            Start a Free Chat
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link to="/blog" className="btn-ghost">
                            <ArrowLeft className="w-4 h-4" />
                            More articles
                        </Link>
                    </div>
                </div>
            </div>
        </article>
    );
}
